import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useUserData } from "../../../hooks/useUserData";
import { swingLibraryService } from "../../../services/swingLibrary";
import { useSwingLibrarySettings } from "./useSwingLibrarySettings";
import { useSwingLibraryStore } from "../store/swingLibraryStore";

export function useSwingList() {
  const { userData } = useUserData();
  const { data: swingLibrarySettings } = useSwingLibrarySettings();
  const { activeFilters, pagination, setPagination, sort } =
    useSwingLibraryStore();

  const { analyzed, cameraAngle, club, date, dominantHand, tags, users } =
    activeFilters;

  const queryKey = [
    "getSwingList_key",
    userData?.uid,
    {
      analyzed,
      cameraAngle,
      club: club.sort().join(","), // Sort to ensure consistent key
      dateRangeEnd: date?.endDate
        ? new Date(date.endDate).setHours(23, 59, 59, 999)
        : null,
      dateRangeStart: date?.startDate
        ? new Date(date.startDate).setHours(0, 0, 0, 0)
        : null,
      dominantHand,
      indicators:
        swingLibrarySettings?.savedIndicators
          ?.map((i) => i.id)
          .sort()
          .join(",") || "",
      order: sort.order,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      sort: sort.sort,
      tags: tags.sort().join(","), // Sort to ensure consistent key
      users: users
        .map((u) => u.userId)
        .sort()
        .join(","), // Sort to ensure consistent key
    },
  ];

  return useQuery({
    enabled: !!userData?.uid,
    queryKey,
    staleTime: 30 * 1000, // 30 seconds - data can change frequently
    queryFn: async () => {
      try {
        const requestBody = {
          indicators: [],
          limit: pagination.rowsPerPage,
          offset: (pagination.page - 1) * pagination.rowsPerPage,
          order: sort.order,
          sort: sort.sort,
          userId: userData?.uid,
        };

        if (users.length) {
          requestBody.userFilter = users;
          requestBody.enterpriseUserId = null;
        } else if (
          userData &&
          userData.subscriptionType.includes("enterprise_master")
        ) {
          requestBody.enterpriseUserId = userData.enterpriseAccountId;
        }
        if (swingLibrarySettings?.savedIndicators?.length)
          requestBody.indicators = swingLibrarySettings.savedIndicators;

        if (date) {
          if (date?.startDate) {
            const startDate = new Date(date.startDate);
            startDate.setHours(0, 0, 0, 0);
            requestBody.dateRangeStart = startDate.valueOf();
          }
          if (date?.endDate) {
            const endDate = new Date(date.endDate);
            endDate.setHours(23, 59, 59, 999);
            requestBody.dateRangeEnd = endDate.valueOf();
          }
        }

        if (club.length) requestBody.club = club;
        if (cameraAngle) requestBody.cameraAngle = cameraAngle;
        if (typeof analyzed === "boolean") requestBody.analyzed = analyzed;
        if (dominantHand) requestBody.domHand = dominantHand;
        if (tags?.length) requestBody.tags = tags;

        const response = await swingLibraryService.getSwingList(requestBody);

        // Update pagination totalPages
        setPagination({
          totalPages: Math.ceil(
            (response?.totalSwings ?? 0) / pagination.rowsPerPage
          ),
        });
        return {
          averages: response?.averages || {},
          totalSwings: response?.totalSwings || 0,
          videos: response?.videos || [],
        };
      } catch (error) {
        toast.error(error?.message ?? "An error occurred while fetching data.");
        throw error;
      }
    },
  });
}
